import React, { useEffect } from 'react';
import { useProducts } from '../hooks/useProducts/useProducts';
import { CollectionTransformer } from '../Contentful/transformers/CollectionTransformer';
import { ProgressiveImage, ProgressiveImageProps } from '../hooks/useProgressiveImage/useProgressiveImage';
import { AlgoliaHitTransformer } from '../Algolia/transformers/AlgoliaHitTransformer';
import { PossiblyResponsiveValue, useResponsiveValue } from '../hooks/useResponsiveValue';
import deepMerge from 'ts-deepmerge';
import { LoadingSkeleton } from '../LoadingSkeleton/LoadingSkeleton';
import { ContentfulCollection } from '@jbhi-fi/apis/src/contentful/types';
import { AssetTransformer } from '../Contentful/transformers/AssetTransformer';
import { ContentfulImage } from '../ContentfulImage/ContentfulImage';
import { useContentfulCollection } from '../hooks/useContentfulCollection/useContentfulCollection';
import { getCachedCollectionImage, setCachedCollectionImage } from './collectionThumbnailCache';

export type CollectionThumbnailProps = {
  sizePx?: PossiblyResponsiveValue<number>;
  className?: string;
  isLoading?: boolean;
} & ({ collection?: ContentfulCollection } | { handle: string });

export const CollectionThumbnail = ({ isLoading, ...props }: CollectionThumbnailProps) => {
  const size = useResponsiveValue(props.sizePx);

  if (!isLoading && 'handle' in props && props.handle) {
    if (getCachedCollectionImage(props.handle)) {
      return (
        <ProgressiveImage
          className={props.className as string}
          {...(getCachedCollectionImage(props.handle) as ProgressiveImageProps)}
          {...(size ? { width: size, height: size } : {})}
        />
      );
    }
    return <CollectionThumbnailFromHandle isLoading={isLoading} {...props} />;
  }

  if ('collection' in props && getCachedCollectionImage(props.collection?.fields?.handle)) {
    return (
      <ProgressiveImage
        className={props.className as string}
        {...(getCachedCollectionImage(props.collection?.fields?.handle) as ProgressiveImageProps)}
        {...(size ? { width: size, height: size } : {})}
      />
    );
  }

  return <CollectionThumbnailFromCollection {...props} />;
};

const CollectionThumbnailFromHandle = ({
  handle,
  isLoading,
  ...props
}: CollectionThumbnailProps & { handle: string }) => {
  const { collection, isLoading: isCollectionLoading } = useContentfulCollection({ handle });
  return (
    <CollectionThumbnailFromCollection
      collection={collection}
      isLoading={isLoading || isCollectionLoading}
      {...props}
    />
  );
};

const CollectionThumbnailFromCollection = ({
  isLoading,
  collection,
  className,
  sizePx: sizePxProp = 200,
}: CollectionThumbnailProps & { collection?: ContentfulCollection }) => {
  const sizePx = useResponsiveValue(sizePxProp);

  if (isLoading || !collection?.fields) {
    return <Skeleton className={className} isLoading={isLoading} sizePx={sizePx} />;
  }

  if (collection.fields.collectionImage?.fields) {
    return (
      <ContentfulImage
        className={className}
        {...AssetTransformer.toContentfulImageProps(collection.fields.collectionImage)}
        width={sizePx}
        sizes={`${sizePx}px`}
        originalWidth={sizePx * 2}
      />
    );
  }
  return <CollectionProductThumbnail collection={collection} sizePx={sizePx} className={className} />;
};

type CollectionProductThumbnailProps = Pick<
  CollectionThumbnailProps & { collection?: ContentfulCollection },
  'collection' | 'className' | 'isLoading'
> & { sizePx: number };

const CollectionProductThumbnail = ({ collection, sizePx, className }: CollectionProductThumbnailProps) => {
  const { products: [product] = [], isLoading } = useProducts(
    deepMerge.withOptions({ mergeArrays: true }, CollectionTransformer.toUseThumbnailProductProps(collection) ?? {}, {
      algoliaSearchOptions: { attributesToRetrieve: ['product_image', 'title', 'sku', 'variant_id'] },
    })
  );

  const imageProps =
    isLoading || !product?.product_image
      ? undefined
      : AlgoliaHitTransformer.toProgressiveImageProps(product, { sizePx: Math.ceil(sizePx * 2) });

  useEffect(() => {
    if (!imageProps || !collection?.fields?.handle) return;

    setCachedCollectionImage(collection.fields.handle, imageProps);
  }, [imageProps, collection]);

  if (!imageProps) {
    return <Skeleton className={className} isLoading={isLoading} sizePx={sizePx} />;
  }

  return (
    <ProgressiveImage
      className={className}
      {...AlgoliaHitTransformer.toProgressiveImageProps(product, { sizePx: Math.ceil(sizePx * 2) })}
      {...(sizePx ? { width: sizePx, height: sizePx } : {})}
    />
  );
};

const Skeleton = ({ isLoading, sizePx, className }: CollectionThumbnailProps) => (
  <LoadingSkeleton.Image className={className} shouldShimmer={isLoading} width={sizePx} border="none" />
);
